// Allowlist of valid environment variable keys that can be read from window.ENV

const ALLOWED_WINDOW_ENV_KEYS = new Set(
  Object.keys(import.meta.env).filter((key) => key.startsWith("VITE_")),
);
// Runtime-only keys that aren't Vite build-time env vars (e.g. injected
// purely via Docker config.js) must be added here manually.
ALLOWED_WINDOW_ENV_KEYS.add("APP_VERSION");
ALLOWED_WINDOW_ENV_KEYS.add("VITE_APP_API_BASE_URL");

export const getEnvValue = (key: string): string => {
  // 1. Runtime config from config.js (injected by entrypoint.sh in Docker)
  // Only allow keys from the allowlist to prevent injection of unexpected values
  if (
    typeof window !== "undefined" &&
    (window as any).ENV?.[key] &&
    ALLOWED_WINDOW_ENV_KEYS.has(key)
  ) {
    return (window as any).ENV[key];
  }
  // 2. Cypress E2E test env, then Vite build-time env
  const value = window?.Cypress
    ? window?.Cypress?.env(key)
    : import.meta.env[key];
  if (!value) {
    throw new Error(`Environment variable "${key}" is not defined`);
  }
  return value as string;
};

export const apiURL = (path = "") => {
  const baseURL = getBaseApiUrl();
  const normalizedPath = path.replace(/^\/+/, "/");
  return new URL(normalizedPath, baseURL);
};

export const isProduction =
  !window?.Cypress && import.meta.env.MODE === "production";
export const isDevelopment =
  !window?.Cypress && import.meta.env.MODE === "development";

const getFallbackApiUrl = (fallbackUrl: string) => {
  try {
    const parsedUrl = new URL(fallbackUrl, window.location.origin);
    return parsedUrl.toString().endsWith("/")
      ? parsedUrl
      : new URL(`${parsedUrl.toString()}/`);
  } catch (error) {
    console.warn(
      `Invalid VITE_APP_API_BASE_URL "${fallbackUrl}". Falling back to current origin.`,
      error,
    );
    return new URL(window.location.origin);
  }
};

export function getBaseApiUrl(): string {
  const { protocol, hostname, origin } = window.location;

  // A raw IP has no tenant host. The frontend nginx on this same origin
  // proxies /api to the backend, so do not use the hosted Trellis fallback.
  const isIpAddress = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);
  if (isIpAddress) {
    return origin.endsWith("/") ? origin : `${origin}/`;
  }

  const fallbackUrl = getEnvValue("VITE_APP_API_BASE_URL");

  const hostParts = hostname.split(".");
  const subdomain = hostParts[0];
  const skipSubdomains = ["app", "www", "admin", "api"];
  const isLocalhost =
    hostname === "localhost" || hostname.endsWith(".localhost");

  const hasTenant =
    Boolean(subdomain) &&
    !skipSubdomains.includes(subdomain) &&
    hostParts.length > 1;

  // For localhost with tenant
  if (isLocalhost && hasTenant) {
    return `${protocol}//${subdomain}.localhost:8000/`;
  }

  // For production with tenant
  if (!isLocalhost && hasTenant) {
    const baseDomain = hostParts.slice(1).join(".");
    const apiUrl = `${protocol}//${subdomain}.${baseDomain}/`;
    return apiUrl;
  }

  const fallback = getFallbackApiUrl(fallbackUrl);
  const isFallbackLoopback =
    ["localhost", "127.0.0.1", "0.0.0.0"].includes(fallback.hostname) ||
    fallback.hostname.endsWith(".localhost");
  const isCurrentLoopback =
    isLocalhost || hostname === "127.0.0.1" || hostname === "0.0.0.0";

  if (isFallbackLoopback && !isCurrentLoopback) {
    fallback.hostname = hostname;
  }

  // Fallback (also used for IP addresses)
  const normalizedUrl = fallback.toString();
  return normalizedUrl;
}

/**
 * Returns the application version.
 * Priority: runtime config.js (Docker) → Vite build-time → "dev".
 */
export function getAppVersion(): string {
  if (
    typeof window !== "undefined" &&
    (window as any).ENV?.APP_VERSION &&
    ALLOWED_WINDOW_ENV_KEYS.has("APP_VERSION")
  ) {
    return (window as any).ENV.APP_VERSION;
  }
  return typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev";
}

/**
 * Resolves a media path to a full URL.
 * - Returns empty string for empty paths.
 * - Returns absolute URLs as-is, except local loopback media URLs opened from another device.
 * - Prepends the base API URL and normalizes duplicate /media/media/ prefixes.
 */
export function getMediaUrl(path: string): string {
  if (!path) return "";

  if (!path.startsWith("/")) {
    try {
      const mediaUrl = new URL(path);
      const baseUrl = new URL(getBaseApiUrl());
      const isLoopbackMediaUrl =
        ["localhost", "127.0.0.1", "0.0.0.0"].includes(mediaUrl.hostname) ||
        mediaUrl.hostname.endsWith(".localhost");

      if (isLoopbackMediaUrl) {
        mediaUrl.hostname = baseUrl.hostname;
      }

      return mediaUrl.toString();
    } catch {
      return path;
    }
  }

  const baseUrl = getBaseApiUrl().replace(/\/$/, "");
  const normalizedPath = path.replace(/^\/+/, "/");
  const cleanPath = normalizedPath.replace(/^\/media\/media\//, "/media/");
  return `${baseUrl}${cleanPath}`;
}
